import {getSessionGeneration} from './sessionGeneration.js'
import {readinessOf, isUncertainOutcome, isReadFailure, reconciliationOf, READ_FAILURE_COPY} from './assignmentSetup.js'

// One synchronous fence per board, shared by all of its action panels. This
// is not authorization: the server rechecks scope and serializes the ledger.
export function createBowlMutation({getMeta, makeRequestId = () => globalThis.crypto?.randomUUID?.(), session = getSessionGeneration, onNotice = () => {}}) {
  const generation = session()
  let active = true, pending = false, uncertain = false, usedVersion = null
  const current = () => active && generation === session()
  const failure = (message, code) => Object.assign(new Error(message), {code})
  return {
    current,
    dispose() { active = false },
    async run(send, body, context = {}) {
      if (!current()) throw failure('This screen no longer belongs to the current session.', 'stale_session')
      if (pending) throw failure('An action is already pending. Wait for its receipt.', 'pending_action')
      if (uncertain) throw failure('The last outcome is uncertain. Refresh and inspect the saved history before taking another action.', 'uncertain_action')
      const meta = getMeta()
      const hasSeason = Number.isInteger(meta?.season) && meta.season >= 1900 && meta.season <= 2200 || /^\d{4}-\d{2}$/.test(String(meta?.season || ''))
      const readiness = readinessOf(meta, { requireVersion: true })
      /* Post-V4.001: a missing assignment roster is setup, not lost history.
       * Still fails closed; only the sentence and the code differ. */
      if (readiness.state === 'setup') {
        throw failure('Assignment setup required. An executive must load the Manage Assignments roster before this can be saved. Nothing was sent.', 'assignment_setup_required')
      }
      if (!hasSeason || readiness.state !== 'ready' || meta?.ready !== true || !Number.isSafeInteger(meta.version) || meta.version < 0 || (meta.pending || []).length) {
        throw failure('Current action history is unavailable or has an unresolved operation. Refresh and inspect the history first.', 'unavailable_context')
      }
      if (usedVersion === meta.version) throw failure('Refresh the board to read the result before taking another action.', 'stale_context')
      const requestId = makeRequestId()
      if (typeof requestId !== 'string' || !requestId) throw failure('A unique action identity could not be created. Nothing was sent.', 'missing_request_id')
      pending = true
      onNotice('An action is pending. Wait for its receipt before another change.')
      try {
        const result = await send({...body, season:meta.season, requestId, expectedVersion: meta.version})
        if (!current()) throw failure('The session or screen changed while the action was pending.', 'stale_session')
        const row = result?.row
        const reusedEmail = context.action === 'email-draft' && Array.isArray(body.to) && body.to.length > 0 && typeof body.subject === 'string' && typeof body.body === 'string'
          && result?.sharedAdmission === true && result.reusedWorkRow === true
          && result.workOperationRequestId === requestId && result.requestedOperationId === requestId
          && /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/.test(result.requestId || '')
          && result.sent === false && typeof result.draftId === 'string' && !!result.draftId
          && row?.kind === 'step' && row.step === 'email' && row.team === body.team
          && typeof result.repId === 'string' && !!result.repId && row.actorId === result.repId
          && typeof row.companyId === 'string' && !!row.companyId
          && row.detail?.draftId === result.draftId && typeof result.mailbox === 'string' && !!result.mailbox && row.detail.mailbox === result.mailbox
        if (result?.ok !== true || result?.durable !== true || !row?.id || (row.requestId !== requestId && !reusedEmail) || String(row.season) !== String(meta.season)) throw failure('No matching durable action receipt was returned.', 'missing_receipt')
        // An operation identity alone is not proof of the school binding the
        // person reviewed. A mismatched receipt is uncertain, never success.
        if (context.action === 'account' && (row.kind !== 'account' || row.team !== body.team
          || typeof body.companyId !== 'string' || !body.companyId.trim()
          || row.companyId !== body.companyId.trim())) throw failure('No matching durable school account receipt was returned.', 'missing_receipt')
        usedVersion = meta.version
        onNotice('The action was saved. Refreshing the board for its current state.')
        return result
      } catch (error) {
        // ⚠️ Dot's review of 390d574: an uncertain write is PRESERVED as "needs
        // reconciliation" with what may have happened, and never collapsed into
        // the setup warning. A server-stated read failure (nothing written) is a
        // retryable read. Anything else unexplained stays uncertain (fail closed).
        if (isReadFailure(error, { forMutation: true }) && current()) {
          onNotice(READ_FAILURE_COPY.action)
          throw Object.assign(failure(READ_FAILURE_COPY.action, 'read_failure'), {status: error?.status, body: error?.body})
        }
        // A pending provider intent is uncertain even when reported as a 409.
        const code = error?.body?.error || error?.code || ''
        uncertain = isUncertainOutcome(error) || error?.body?.retrySafe === false
          || error?.body?.readFailure === true
          || ![400, 401, 403, 404, 409, 422].includes(error?.status)
          || /pending|uncertain|in_progress/.test(code)
        if (uncertain && current()) {
          const rec = reconciliationOf(error)
          onNotice(rec.text)
          throw Object.assign(failure(rec.text, 'uncertain_action'), {status: error?.status, body: error?.body, reconciliation: rec})
        }
        if (current()) onNotice('The action was refused. No confirmed success receipt was returned.')
        throw error
      } finally { pending = false }
    },
  }
}
